/*
 * VatomInc. Copyright (c) 2022, all rights reserved.
 *
 * This source code is licensed under the license found in the LICENSE file in the root directory of this source tree.
 */
import EventEmitter from './EventEmitter'

/**
 * Operates a PubSub protocol over a WebSocket. See github.com/vatominc/spatialweb-pubsub for more info.
 *
 * @event message When a joined channel receives a message. Object includes a `channel` string and a `data` object.
 */
export default class WebSocketPubSub extends EventEmitter {
  /** Message queue to be sent */
  queue = []

  /** Current listening channels */
  channels = []

  /** Constructor */
  constructor(url) {
    super()

    // Store URL
    this.url = url

    // Connect immediately
    this.connect()
  }

  /** @private Connect the WebSocket */
  connect() {
    // Open the socket
    this.ws = new WebSocket(this.url)
    this.ws.onopen = this.onOpen.bind(this)
    this.ws.onclose = this.onClose.bind(this)
    this.ws.onerror = err => console.warn(`[WebSocket PubSub] Connection error: ${err}`) //this.onError.bind(this)
    this.ws.onmessage = this.onMessage.bind(this)
  }

  /** @private Called when the WebSocket opens */
  onOpen() {
    // Connection opened
    console.debug(
      `[WebSocket PubSub] Socket connected, sending ${this.queue.length} queued msgs and joining ${this.channels.length} channels.`
    )

    // Send config
    this.ws.send(
      JSON.stringify({
        action: 'setup',
        batchedMessages: true
      })
    )

    // Rejoin our channels
    for (let channel of this.channels) this.ws.send(JSON.stringify({ action: 'join', channel }))

    // Send all our queued messages
    for (let msg of this.queue) this.ws.send(msg)
    this.queue = []
  }

  /** @private Called when the WebSocket connection drops */
  onClose(err) {
    // Show it
    console.warn(`[WebSocket PubSub] Socket closed, reconnecting soon...`)

    // Connect again soon
    clearInterval(this.retryTimer)
    this.retryTimer = setTimeout(e => this.connect(), 2000)
  }

  /** Shutdown */
  shutdown() {
    // Close socket
    if (this.ws) {
      this.ws.onclose = null
      this.ws.close()
    }
  }

  /** @private Called when a message is received */
  onMessage(e) {
    // Get data
    let msgs = JSON.parse(e.data)

    // Trigger it
    for (let msg of msgs) this.emit('message', msg)
  }

  /** Join a channel */
  join(channelName) {
    // Add to list
    if (!this.channels.includes(channelName)) this.channels.push(channelName)

    // Send message if socket is open
    if (this.ws.readyState == this.ws.OPEN)
      this.ws.send(JSON.stringify({ action: 'join', channel: channelName }))
  }

  /** Leave a channel */
  leave(channelName) {
    // Add to list
    this.channels = this.channels.filter(c => c != channelName)

    // Send message if socket is open
    if (this.ws.readyState == this.ws.OPEN)
      this.ws.send(JSON.stringify({ action: 'leave', channel: channelName }))
  }

  /** Post a message to a channel */
  post(channelName, message) {
    // Get data to send
    let payload = JSON.stringify({
      action: 'post',
      channel: channelName,
      data: message
    })

    // Check if socket is open
    if (this.ws.readyState == this.ws.OPEN) {
      // Post it now
      this.ws.send(payload)
    } else {
      // Add to queue
      this.queue.push(payload)
    }
  }
}
